import React, { useEffect, useState } from "react";
import { X, Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import GoogleButton from "./GoogleButton";
import AccountBlockedDialog, { blockedInfo } from "./AccountBlockedDialog";

export default function AuthModal({ open, onClose }) {
  const { login, register, formatApiError } = useAuth();
  const { google_client_id } = useConfig();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      onClose();
      setName(""); setEmail(""); setPassword("");
    } catch (e2) {
      const d = e2.response?.data?.detail;
      if (blockedInfo(d)) setBlocked(d); else setErr(formatApiError(d) || "Something went wrong");
    } finally { setBusy(false); }
  };

  const inp = "w-full pl-10 pr-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]";

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="auth-modal" className="relative w-full max-w-md card-surface rounded-2xl p-7">
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-md text-white/40 hover:text-white hover:bg-white/5"><X size={18} /></button>
        <div className="flex items-center gap-2 mb-1">
          <span className="font-head text-2xl font-bold" style={{ color: "hsl(var(--accent))" }}>7</span>
          <span className="font-head text-xl font-bold text-white">LEGENDARY</span>
        </div>
        <p className="text-sm text-white/50 mb-5">{mode === "login" ? "Welcome back. Sign in to your account." : "Create your free account to join the community."}</p>

        <div className="flex gap-1 p-1 rounded-lg bg-black/30 mb-5">
          {["login", "register"].map((m) => (
            <button key={m} onClick={() => { setMode(m); setErr(""); }} data-testid={`auth-tab-${m}`}
              className={`flex-1 py-2 rounded-md text-sm font-semibold transition-colors ${mode === m ? "bg-white text-black" : "text-white/60 hover:text-white"}`}>
              {m === "login" ? "Sign In" : "Register"}
            </button>
          ))}
        </div>

        <AccountBlockedDialog info={blocked} onClose={() => setBlocked(null)} />
        {google_client_id && (
          <>
            <GoogleButton onDone={onClose} onError={(m) => (blockedInfo(m) ? setBlocked(m) : setErr(formatApiError(m)))} />
            <div className="flex items-center gap-3 my-4 text-[11px] uppercase tracking-widest text-white/30"><span className="flex-1 h-px bg-white/10" />or with email<span className="flex-1 h-px bg-white/10" /></div>
          </>
        )}
        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div className="relative">
              <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Display name" className={inp} />
            </div>
          )}
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className={inp} />
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="Password (min 6 chars)" className={inp} />
          </div>
          {err && <div data-testid="auth-error" className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2">{err}</div>}
          <button type="submit" disabled={busy} data-testid="auth-submit"
            className="w-full py-3 rounded-lg bg-[hsl(var(--primary))] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />}
            {mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>
      </div>
    </div>
  );
}
